import store from '@/store'
import ProntoStorage from './storage'
import { detect } from 'detect-browser'
import { LOG_TABLE_NAME } from './constants'
import {
	dbPut,
	importTable,
	countTableRows,
	deleteTableRows,
	writeToLog,
	getFileBlob,
	dumpLogsToFile,
	deleteFile,
} from '@/store/database'
import { compressBlob, downloadBlob } from './files'
import Pusher from 'pusher-js'
import posthog from 'posthog-js'

const storage = new ProntoStorage('localStorage')
const sStorage = new ProntoStorage('sessionStorage')

const MAX_CAPTURED_LOGS = 5000 // logs will be trimmed to this number of rows
const LOG_TRIM_FREQUENCY = 1 / 1000 // logs will be trimmed this percentage of the time
const storeProperties = [
	'messages',
	'memberships',
	'chats',
	'announcements',
	'uploads',
]

const globalExceptionListener = (event, file, line, col) => {
	const err = event?.error
	const payload = {
		message: event?.message || err?.message || String(event),
		stack: err?.stack,
		name: err?.name,
		file: file || event?.filename,
		line: line ?? event?.lineno,
		col: col ?? event?.colno,
	}
	captureLog('JSException', payload)
	posthog.capture('JSException', payload)
}
const globalRejectionListener = (event) => {
	const reason = event?.reason
	let payload
	if (reason instanceof Error) {
		payload = {
			message: reason.message,
			stack: reason.stack,
			name: reason.name,
			code: reason.code,
		}
	} else if (typeof reason === 'object' && reason !== null) {
		try {
			payload = { reason: JSON.stringify(reason) }
		} catch {
			payload = { reason: String(reason) }
		}
	} else {
		payload = { reason: String(reason) }
	}
	captureLog('JSRejection', payload)
	posthog.capture('JSRejection', payload)
}

export const globalVueErrorListener = (error, vm, info) => {
	const component = vm.$options?._componentTag || 'Unknown'
	captureLog(
		'VueException',
		`Component: ${component} in ${info}\n`,
		error.stack
	)
	posthog.capture('VueException', { component, info, error: error.stack })
}
const keyboardListener = (e) => {
	if (e.code === 'KeyL' && e.ctrlKey && e.altKey) {
		e.preventDefault()
		downloadLogFile()
	}
}
const onlineListener = () => {
	captureLog(
		'Browser',
		'Online status change:',
		window.navigator.onLine ? 'Online' : 'Offline'
	)
}

export const trimLogCache = async (max = MAX_CAPTURED_LOGS) => {
	if (storage.has('pronto_stop_log_trim')) {
		return console.debug(
			'bailing instead of trimming logs',
			max,
			'current row count:',
			await countTableRows(LOG_TABLE_NAME)
		)
	}
	console.debug(`Starting to trim logs to max of ${max}...`)
	const rowsToKeep = await importTable(LOG_TABLE_NAME, {
		orderBy: 'id',
		reverse: true,
		limit: MAX_CAPTURED_LOGS,
	})
	if (!rowsToKeep.length) return normalDebug('No logs to trim')
	const minIdToKeep = rowsToKeep.at(-1).id
	await deleteTableRows(LOG_TABLE_NAME, { where: 'id', below: minIdToKeep })
	console.debug('Completed trimming logs')
}

const origPusherLogger = {}
const startCapturingPusherLogs = () => {
	if (!window.Pusher) {
		console.error(
			'Pusher: could not capture logs because pusher not initialized'
		)
		return
	}
	if (window.Pusher?.log) {
		origPusherLogger.instance = window.Pusher?.log
	}
	window.Pusher.log = function PusherLogger(message) {
		origPusherLogger.instance?.(message)
		if (!message) return
		message = message.replace('Pusher :  :', '').trim()
		// pusher logs out these 'no callbacks' errors on every event because we are binding
		// globally not at the channel level. We'll just ignore them for now.
		if (message?.startsWith('["No callbacks on')) return
		try {
			// check to see if there is json embedded in the message
			const [name, payload] = JSON.parse(message)
			if (payload?.event && payload?.channel) {
				captureLog('Pusher', payload.event, payload.channel, payload.data)
			} else {
				captureLog('Pusher', name, payload)
			}
		} catch (e) {
			// no json so just log the message as plain text
			normalDebug('pusher parse bug', e)
			captureLog('Pusher', message)
		}
	}
}

const stopCapturingPusherLogs = () => {
	window.Pusher.log = origPusherLogger.instance ?? null
	captureLog('Pusher', 'stopped capturing logs')
}

export const startCapturingConsole = () => {
	if (window.console.prontoCapturing) return false
	// eslint-disable-next-line no-console
	console.log('Starting to capture logs...')
	window.addEventListener('error', globalExceptionListener)
	window.addEventListener('unhandledrejection', globalRejectionListener)
	window.addEventListener('keyup', keyboardListener)
	window.addEventListener('online', onlineListener)
	window.addEventListener('offline', onlineListener)
	// Vue.config.errorHandler = globalVueErrorListener
	startCapturingPusherLogs()
	Object.entries(window.console)
		.filter(([key, val]) => typeof val === 'function')
		.forEach(([key, val]) => {
			window.console[`orig_${key}`] = val
			window.console[key] = (...args) => {
				if (window.appConfig?.suppress_errors && key === 'error') {
					// eslint-disable-next-line no-console
					console.orig_log('suppressing logs due to appconfig params')
					captureLog('debug', ...args)
					window.console.orig_debug(...args)
				} else {
					captureLog(key, ...args)
					posthog.capture(key, args)
					val(...args)
				}
			}
		})
	window.console.prontoCapturing = true
	captureLog('************* STARTING TO CAPTURE LOGS ***************')
	return true
}

export const stopCapturingConsole = () => {
	window.removeEventListener('error', globalExceptionListener)
	window.removeEventListener('unhandledrejection', globalRejectionListener)
	window.removeEventListener('keyup', keyboardListener)
	window.removeEventListener('online', onlineListener)
	window.removeEventListener('offline', onlineListener)
	stopCapturingPusherLogs()
	Object.entries(window.console)
		.filter(([key]) => key.startsWith('orig_'))
		.forEach(([key, val]) => {
			delete window.console[key]
			const origKey = key.replace('orig_', '')
			window.console[origKey] = val
		})
	// eslint-disable-next-line no-console
	console.log('Stopped capturing logs')
	delete window.console.prontoCapturing
	return true
}

export const getCapturedLogs = () => {
	return importTable(LOG_TABLE_NAME)
}

export const getLogFileName = () => {
	const { user } = store.auth || {}
	const username = user?.fullname ?? 'Unknown_User'
	const timestamp = getTimestamp(true)
	return `${username}_${timestamp}`
		.split(/[/:_"' ]/)
		.filter((x) => x)
		.join('_')
}

// The exported file is handed to support, so live credentials must never
// appear in it. Scrubbing here (rather than only at capture time) also covers
// rows captured before redaction existed and the device-info preface, which
// serializes cookies, localStorage and the pusher config wholesale.
const liveSecrets = () => [
	store.auth?.api_token,
	store.auth?.media_token?.accessToken,
	store.auth?.integration_jwt?.token,
]

export const scrubSecrets = (text, secrets = liveSecrets()) =>
	secrets
		.filter(Boolean)
		.reduce(
			(scrubbed, secret) =>
				scrubbed
					.replaceAll(secret, '[redacted]')
					.replaceAll(encodeURIComponent(secret), '[redacted]'),
			text
		)

export const downloadLogFile = async () => {
	let closeAlert = null
	try {
		closeAlert = window.prontoAlert.success('Starting to process logs...', {
			delay: 10000,
		})
		const tempFileName = `${Date.now()}.txt`
		const STORAGE = await navigator.storage.estimate()
		const preface = getDeviceInfo({ STORAGE })
		await dumpLogsToFile(LOG_TABLE_NAME, { fileName: tempFileName, preface })
		let { blob } = await getFileBlob(tempFileName)
		blob = new Blob([scrubSecrets(await blob.text())])
		blob = await compressBlob(blob)
		const fileName = `${getLogFileName()}.tsv.gz`
		await downloadBlob(blob, fileName)
		await deleteFile(tempFileName)
		window.prontoAlert.success('Log file downloaded successfully')
	} catch (e) {
		window.console.orig_error('Error while processing logs', e)
		window.prontoAlert.error('Error while processing logs')
	} finally {
		closeAlert?.()
	}
}

// userKey is the AES passphrase for the encrypted values stored beside it
// (api_token, integration_jwt, user) — exporting key and ciphertext together
// would hand out the credentials regardless of the value-match scrub above.
// media_token is stored in cleartext outright.
const REDACTED_STORAGE_KEYS = [
	'userKey',
	'api_token',
	'integration_jwt',
	'media_token',
]

export const getDeviceInfo = (additionalInfo = {}) => {
	const [pusher] = window.Pusher?.instances || []
	const { user, pusherTransportTLS, ...storage } = window.localStorage
	const userOverride = user ? `${user.substring(0, 100)}...<obfuscated>` : null
	const localStorageData = Object.assign({}, storage, {
		user: userOverride,
	})
	REDACTED_STORAGE_KEYS.filter((key) => key in localStorageData).forEach(
		(key) => (localStorageData[key] = '[redacted]')
	)
	const cookies = document.cookie
		.split('; ')
		.map((c) => c.split('='))
		.reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {})
	if (cookies.api_token) cookies.api_token = '[redacted]'
	const info = {
		DEVICE_INFO: {
			currentTime: new Date(),
			userId: store.auth.user?.id,
			orgId: store.auth.user?.organizations?.[0]?.id,
			browser: detect(),
			appConfig: window.appConfig,
			userAgent: window.navigator.userAgent,
			url: window.location.href,
		},
		PUSHER_INFO: {
			options: pusher?.timeline?.options || null,
			config: pusher?.config || null,
			transport: pusherTransportTLS,
		},
		LOCALSTORAGE: localStorageData,
		COOKIES: cookies,
		...additionalInfo,
	}
	if (window.todesktop) {
		const { version, electronVersion, os } = window.todesktop
		info.TODESKTOP = { version, electronVersion, os }
	}
	return Object.entries(info).map(([key, data]) => [
		getTimestamp(),
		key,
		JSON.stringify(data),
	])
}

const getStoreData = () => {
	if (!window._store) return []
	return storeProperties.map((prop) => {
		const data = window._store[prop]?.[`${prop}Data`] || {}
		return [getTimestamp(), prop.toUpperCase(), JSON.stringify(data)].join('\t')
	})
}

const normalDebug = (...args) => {
	// eslint-disable-next-line no-console
	if (!console.prontoCapturing) return // eslint-disable-next-line no-console
	;(console.orig_debug ?? console.debug)(...args)
}

const getTimestamp = (omitMS) => {
	const now = new Date()
	const date = now.toLocaleDateString()
	const time = now.toLocaleTimeString('en-US', { hour12: false })
	const dateTime = `${date} ${time}`
	if (omitMS) return dateTime
	return `${dateTime}:${now.getMilliseconds()}`
}

export const createLogger = (prefix, logToConsole = true) => {
	if (!prefix.startsWith('[')) {
		prefix = `[${prefix}]`
	}
	return (...args) => {
		if (logToConsole) {
			normalDebug(prefix, ...args)
		}
		captureLog(prefix, ...args)
	}
}

const shouldTrimLogs = () => {
	return Math.random() <= LOG_TRIM_FREQUENCY
}

const isObject = (val) => typeof val === 'object' && val !== null
const stringify = (obj) => {
	try {
		return JSON.stringify(obj)
	} catch (e) {
		return null
	}
}

export const timedLogger = (label, level = 'debug') => {
	const start = Date.now()
	let lastLogTime = start
	label = label.toUpperCase()
	const segments = []
	const logger = (segment = 'next', ...data) => {
		const now = Date.now()
		const total = now - start
		const diff = now - lastLogTime
		const seg = { segment, total, diff, time: now }
		if (data.length) seg.data = data
		segments.push(seg)
		lastLogTime = now
	}
	logger.print = () => {
		// eslint-disable-next-line no-console
		if (!console.prontoCapturing) return false
		// eslint-disable-next-line no-console
		console[level](label, JSON.stringify(segments))
	}
	return logger
}

export const captureLog = (...args) => {
	// TODO: remove this when off compatibility build
	if (args.some((str) => str?.includes?.('Avoid app logic that relies')))
		return false
	// if 3rd party cookies (and storage) are disabled, just bail
	// eslint-disable-next-line no-console
	if (!storage.hasAccess || !console.prontoCapturing) return false
	// only capture logs if user is currently persisting their cache
	const persist = storage.get('persist')
	if (persist !== true && persist !== 'true') return
	const timestamp = getTimestamp()
	// insert log into the database with a fake (but unique) id
	const log = [timestamp, ...args].map((item) => {
		if (item instanceof Error) return item.stack || item.message
		if (item instanceof Element) return printElement(item)
		if (item?.pusher instanceof Pusher) {
			return Object.assign({}, item, {
				pusher: '[omitted]',
				callbacks: '[omitted]',
			})
		}
		if (!isObject(item)) return item
		return stringify(item) || '[Unserializable]'
	})
	if (storage.has('pronto_enable_file_logging')) {
		writeToLog([log.join('\t')])
	}
	dbPut(LOG_TABLE_NAME, { log })
	if (shouldTrimLogs()) {
		trimLogCache()
	}
}

export const printElement = (elem) => {
	const tag = elem.tagName.toLocaleLowerCase()
	const classes = Array.from(elem.classList).join('.')
	const id = elem.id ? `#${elem.id}` : ''
	const text = elem.textContent
	if (!text) return `<${tag}${id}${classes ? `.${classes}` : ''} />`
	return `<${tag}${id}${classes ? `.${classes}` : ''}>${text}</${tag}>`
}
